// Workflows List Page
import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, FolderOpen } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loading } from '../components/ui/loading';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuthStore } from '../stores/authStore';
import { useWorkflowsStore } from '../stores/workflowsStore';

export default function WorkflowsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { workflows, loading, error, fetchWorkflows, deleteWorkflow } = useWorkflowsStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // NAV-11: storeSubscriptions ya carga workflows al login. Este efecto asegura
  // datos frescos al navegar a la página, pero evita fetch redundante en el primer
  // montaje si los datos ya están cargados (previene flash del spinner).
  const skipFirstFetchRef = useRef(workflows.length > 0);
  useEffect(() => {
    if (!user) return;
    if (skipFirstFetchRef.current) {
      skipFirstFetchRef.current = false;
      return;
    }
    fetchWorkflows(user.uid);
  }, [user, fetchWorkflows]);

  const handleDelete = (id: string) => {
    setWorkflowToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (workflowToDelete) {
      setIsDeleting(true);
      try {
        await deleteWorkflow(workflowToDelete);
        // H-F032: Toast de confirmación después de eliminar
        const { getToastInstance } = await import('../lib/toastHelper');
        getToastInstance()?.success('Workflow eliminado');
      } catch {
        // Error toast ya se muestra desde el store
      } finally {
        setIsDeleting(false);
      }
    }
    setDeleteDialogOpen(false);
    setWorkflowToDelete(null);
  };

  const filteredWorkflows = workflows.filter((workflow) =>
    workflow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    workflow.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-8 space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400" aria-label="Navegación">
        <Link to="/dashboard" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">Dashboard</Link>
        <span aria-hidden="true">/</span>
        <span className="text-gray-900 dark:text-gray-100 font-medium">Workflows</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Mis Workflows</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Gestiona tus workflows de automatización</p>
        </div>
        <Button onClick={() => navigate('/workflows/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Workflow
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <Input
            type="search"
            placeholder="Buscar workflows..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div role="alert" className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && workflows.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-4" />
              <div className="flex gap-2">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-12" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            {searchQuery ? (
              // Hay búsqueda activa pero sin resultados
              <>
                <Search className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  No se encontraron workflows para &ldquo;{searchQuery}&rdquo;
                </p>
                <Button variant="outline" onClick={() => setSearchQuery('')}>
                  Limpiar búsqueda
                </Button>
              </>
            ) : (
              // No hay workflows aún
              <>
                <FolderOpen className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">No hay workflows todavía</p>
                <Button onClick={() => navigate('/workflows/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Primer Workflow
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkflows.map((workflow) => (
            <Card key={workflow.id} className="hover:shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all cursor-pointer" onClick={() => navigate(`/workflows/${workflow.id}`)}>
              <CardHeader>
                <div>
                  <CardTitle className="text-lg">{workflow.name}</CardTitle>
                  {workflow.description && (
                    <CardDescription className="mt-1">
                      {workflow.description}
                    </CardDescription>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {workflow.nodes.length} pasos
                    </Badge>
                    {workflow.executionCount > 0 && (
                      <Badge variant={workflow.successCount > workflow.failureCount ? 'success' : 'default'}>
                        {workflow.successCount}/{workflow.executionCount}
                      </Badge>
                    )}
                  </div>
                  {/* I-03: stopPropagation para evitar doble navegación con el onClick de la Card */}
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/workflows/${workflow.id}`); }} aria-label="Editar workflow" title="Editar">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); handleDelete(workflow.id); }}
                      aria-label="Eliminar workflow"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent role="alertdialog" aria-describedby="delete-dialog-desc">
          <DialogHeader>
            <DialogTitle>Eliminar Workflow</DialogTitle>
            <DialogDescription id="delete-dialog-desc">
              ¿Estás seguro de eliminar este workflow? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loading size="sm" className="mr-2" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

